import { DateTime as DT } from 'luxon';
import { createPortal } from 'react-dom';
import { useWarehouse } from './context/WarehouseProvider';
import { ProductTitle } from 'modules/product';
import CountBadge from './CounBadge';
import { Button, Dropdown } from 'react-bootstrap';
import { format$Price, formatPrice } from 'utils/helpers/functions/formatPrice';
import { withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { canWarehouseOps } from 'utils/helpers/access/hasUserAccess';
import { useTranslation } from 'react-i18next';
import vconst from 'utils/helpers/structs/vconst';
import EToolTip from 'components/others/EToolTip';
import CopyTextWrapper from 'components/others/CopyTextWrapper';
import SolarIcon from 'components/icons/SolarIcon';

const formatStockAmount = (amount: number, isUahMode: boolean) => {
    if (isUahMode) {
        return amount >= 0
            ? withLocalCurrency(formatPrice(amount))
            : withLocalCurrency(formatPrice(-amount), true);
    }
    return format$Price(amount);
};

const StockTableRows = ({
    wId,
    stock,
    setShowTransfer,
    setShowComing,
    setShowPaymentsId,
    setShowPaymentsTitle,
    onlyView,
}: any) => {
    const { setWOperation, showCredit } = useWarehouse();
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const isUahMode = (Number(localSettings?.RATE?.current ?? 1) || 1) === 1;
    const { t } = useTranslation(['Warehouses']);
    const canOps = canWarehouseOps(auth?.user);
    const canCredit = Boolean(params.creditControl);
    const dtU = DT.fromMillis(stock?.stockUpdated || 0).toFormat('dd.MM.yyyy');
    const isMainWarehouse = wId === vconst.waherouseMainId;
    const showCreditAmount =
        Boolean(stock.stockBalance) &&
        showCredit &&
        auth?.user?.access?.role == 'manager' &&
        isMainWarehouse;

    const creditAmountNode = showCreditAmount ? (
        <span className={`fs-12 ${stock.stockBalance < 0 ? 'text-warning' : 'text-danger'}`}>
            {formatStockAmount(-stock.stockBalance, isUahMode)}
        </span>
    ) : null;

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { variant?: 'danger' },
    ) => (
        <Dropdown.Item
            as="button"
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.variant === 'danger' ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.variant === 'danger' ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    return (
        <tr>
            <td className="name">
                <ProductTitle
                    product={stock}
                    onClick={() => {
                        setWOperation({
                            warehouseId: stock?.warehouseId,
                            productId: stock?.productId,
                            stockCount: stock?.stockCount,
                        });
                    }}
                />
            </td>
            <td className="article text-truncate">
                <CopyTextWrapper value={stock.prodArticle || ''}>
                    <span title={stock.prodArticle}>{stock.prodArticle}</span>
                </CopyTextWrapper>
            </td>
            <td className="count">
                <CountBadge fs={12} count={stock.stockCount} />
                {creditAmountNode && <span className="d-block pt-2 d-lg-none">{creditAmountNode}</span>}
            </td>
            {showCredit &&
                auth?.user?.access?.role == 'manager' &&
                isMainWarehouse && (
                    <td className="balance text-end">
                        {creditAmountNode && canCredit ? (
                            <span
                                className="cursor-pointer"
                                role="button"
                                title={t('toolPayments')}
                                onClick={() => {
                                    setShowPaymentsId(stock?.productId);
                                    setShowPaymentsTitle(stock?.prodName);
                                }}
                            >
                                {creditAmountNode}
                            </span>
                        ) : (
                            creditAmountNode
                        )}
                    </td>
                )}
            <td className="updated">
                <span className="cursor-pointer text-break">{dtU}</span>
            </td>
            {!onlyView && canOps && (
                <td className="action">
                    <div className="d-flex align-items-center justify-content-end gap-1">
                        {!isMainWarehouse ? (
                            <EToolTip title={t('toolTransfer')}>
                                <span className="d-inline-block">
                                    <Button
                                        className="p-1 py-0"
                                        size="sm"
                                        variant="light-secondary"
                                        onClick={(e) => {
                                            e.currentTarget.blur();
                                            setShowTransfer(stock?.productId);
                                        }}
                                    >
                                        <SolarIcon name="transfer" className="p-0" />
                                    </Button>
                                </span>
                            </EToolTip>
                        ) : (
                            <Dropdown align="end">
                                <Dropdown.Toggle
                                    className="p-1 py-0 warehouse-team-menu-toggle"
                                    size="sm"
                                    variant="light-secondary"
                                    id={`dropdown-stock-${stock.ID}`}
                                >
                                    <SolarIcon name="three-dots" />
                                </Dropdown.Toggle>
                                {typeof document !== 'undefined'
                                    ? createPortal(
                                          <Dropdown.Menu
                                              className="p-2 shadow-sm warehouse-team-menu"
                                              popperConfig={{ strategy: 'fixed' }}
                                              renderOnMount
                                          >
                                              {renderMenuItem(
                                                  <SolarIcon name="transfer" />,
                                                  t('toolTransfer'),
                                                  t('toolTransferHint'),
                                                  () => setShowTransfer(stock?.productId),
                                              )}
                                              {renderMenuItem(
                                                  <SolarIcon name="box-arrow-in-down" />,
                                                  t('toolComming'),
                                                  t('toolCommingHint'),
                                                  () => setShowComing(stock?.productId),
                                              )}
                                              {canCredit ? (
                                                  <>
                                                      <Dropdown.Divider className="my-2" />
                                                      {renderMenuItem(
                                                          <SolarIcon name="cash-coin" />,
                                                          t('toolPayments'),
                                                          t('toolPaymentsHint'),
                                                          () => {
                                                              setShowPaymentsId(stock?.productId);
                                                              setShowPaymentsTitle(stock?.prodName);
                                                          },
                                                      )}
                                                  </>
                                              ) : null}
                                          </Dropdown.Menu>,
                                          document.body,
                                      )
                                    : null}
                            </Dropdown>
                        )}

                    </div>
                </td>
            )}
        </tr>
    );
};

export default StockTableRows;
